import { Injectable, Optional } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { forkJoin, from, Observable, of } from 'rxjs';
import { AuthService } from './../services/auth.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { NbToastrService } from '@nebular/theme';
import { environment } from './../../environments/environment';
import { ShopService } from '../shop/shop.service';
import { ShopSettings } from 'shared';
import { AppCheck, AppCheckTokenResult, getToken } from '@angular/fire/app-check';

@Injectable()
export class FirebaseAuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private shopService: ShopService,
    private toastService: NbToastrService,
    @Optional() private appCheck: AppCheck
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Get the auth token from the service.
    let shopSettings: ShopSettings = {} as ShopSettings;

    // Dropship (Online Exclusives) endpoints are public reads/cart ops with no user
    // auth, but still first-party traffic — attach an App Check token so the
    // dropship_svc AppCheckGuard accepts them. Kept separate from the shop branch:
    // no spinner/success-toast, since these are background catalog fetches, not user
    // actions, and we don't want to swallow errors the exclusives service handles.
    if (req.url.indexOf('dropship-api') >= 0) {
      return this.appCheckToken$().pipe(
        switchMap((appCheckTok) =>
          next.handle(
            req.clone({
              headers: req.headers.set('X-Firebase-AppCheck', appCheckTok?.token || '')
            })
          )
        )
      );
    }

    return req.url.indexOf('shop-api') < 0
      ? next.handle(req)
      : from(this.shopService.shopSettings$()).pipe(
          switchMap((settings) => {
            shopSettings = settings;
            return forkJoin({
              tok: from(this.authService.getIdTokenResult()),
              appCheckTok: this.appCheckToken$()
            });
          }),
          switchMap(({ tok, appCheckTok }) => {
            // Clone the request and replace the original headers with
            // cloned headers, updated with the authorization.
            this.spinner.show();
            const authReq = req.clone({
              headers: req.headers
                .set('Authorization', `Bearer ${tok?.token || ''}`)
                .set('X-Firebase-AppCheck', appCheckTok?.token || '')
                .set(
                  'Stripe-Environment',
                  environment.production ? 'production' : environment.qa ? 'qa' : 'development'
                )
                .set('Shop-Enabled', shopSettings.isShopEnabled.toString())
                .append('Content-Type', 'application/json')
            });

            // send cloned request with header to the next handler.
            return next.handle(authReq);
          }),
          tap((evt) => {
            if (evt instanceof HttpResponse) {
              // response
              this.spinner.hide();
              this.toastService.success(`${evt.body?.message}`, 'Success', { duration: 3000 });
            }
          }),
          catchError((err) => {
            if (err instanceof HttpErrorResponse) {
              this.spinner.hide();
              if (err && err.status > 499) {
                this.toastService.danger(`${err.error?.message}`, 'Whoops!', { duration: 6000 });
              } else {
                this.toastService.info(`${err.error?.message}`, 'Issue', { duration: 7000 });
              }
            }
            return of(err.error);
          })
        );
  }

  // Mints an App Check token, or null when App Check isn't available (SSR) or the
  // token fails to resolve. Shared by the shop and dropship request branches.
  private appCheckToken$(): Observable<AppCheckTokenResult | null> {
    return this.appCheck
      ? from(getToken(this.appCheck, false)).pipe(catchError(() => of(null)))
      : of(null);
  }
}
